import { useTranslation } from "react-i18next"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { useFormSubmit } from "shared/lib/useFormSubmit"

import { useDuplicateFloor } from "entities/floors/api/useDuplicateFloor"
import { useFloorFragment } from "entities/floors/model/useFloorFragment"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { FloorFormModel } from "features/floor-management/types/FloorFormModel"
import { useFloorForm } from "widgets/floor/floor-form/model/useFloorForm"

interface UseCopyFloorOverlayModelArgs {
  floorID: string
  projectID: string
}

export function useCopyFloorOverlayModel({
  floorID,
  projectID
}: UseCopyFloorOverlayModelArgs) {
  const { t } = useTranslation()
  const floor = useFloorFragment(floorID)
  const [duplicateFloor] = useDuplicateFloor()
  const { closeOverlay } = useCurrentOverlay()

  const submitCopy = useEvent(async (formData: FloorFormData) => {
    const targetProjectID = formData.projectID || projectID
    try {
      const result = await duplicateFloor({
        floorID,
        projectID: targetProjectID,
        position: formData.floor?.position,
        label: formData.floor?.label ?? undefined
      })
      return { data: result ?? null }
    } catch (error) {
      return { data: null, errors: [{ message: String(error) }] }
    }
  })

  const { handleSubmit, isSubmitting } = useFormSubmit<FloorFormData>({
    onSubmit: submitCopy,
    onSuccess: closeOverlay
  })

  const floorFormModel: FloorFormModel = useFloorForm({
    floor,
    projectID,
    parentID: projectID,
    onSubmit: handleSubmit,
    formOptions: { hideProject: false, hideClarificationRequest: true }
  })

  return {
    floorFormModel,
    headline: t("Floor.Actions.Copy"),
    isLoading: isSubmitting,
    isSubmitting,
    onCancel: closeOverlay
  }
}
